import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HARDWARE_LABEL, ISSUE_TEXT, STATUS_STYLE, ago, codeVersionOf, driftOf, isEmulator, modelsOf } from "../lib/fleet";

// Every cabinet in one table (docs/tasks/35): what the sales platform's list
// shows, plus what only staff need - the models and engines it runs, its
// software, its board's condition. One row per cabinet; click for its page.

const ORDER = { service: 0, offline: 1, degraded: 2, unknown: 3, online: 4, revoked: 5 };

function gb(bytes) {
  return bytes == null ? "—" : `${(bytes / 1e9).toFixed(1)}`;
}

function row(c, newest) {
  const h = c.health || {};
  const r = h.report || {};
  const sys = r.system || {};
  const m = modelsOf(h);
  const door = r.door || {};
  const cams = r.cameras || {};
  const t = c.temperature;
  return {
    c, id: c.id,
    name: c.device?.name || c.id,
    type: c.type?.name || (isEmulator(c) ? "emulator" : "—"),
    board: HARDWARE_LABEL[c.device?.hardware] || c.device?.hardware || "—",
    status: h.service?.active ? "service" : h.status || (c.device?.revoked ? "revoked" : "unknown"),
    problems: [...(h.issues || []), ...(h.warnings || [])].map((k) => ISSUE_TEXT[k] || k).join("; "),
    seen: h.last_seen_at || c.device?.last_seen_at || 0,
    door: door.state ? `${door.state}${door.locked ? " · locked" : ""}${door.layout === "pair" ? " · 2 locks" : ""}` : "—",
    charge: door.charge ? (door.charge.relay_stuck ? "relay stuck" : door.charge.on ? "on" : "off") : "",
    session: r.session ? r.session.state : "—",
    cameras: cams.expected != null ? `${cams.healthy}/${cams.expected}` : "—",
    camError: cams.error || (cams.expected > 0 && cams.healthy < cams.expected ? `${cams.healthy} of ${cams.expected} working` : ""),
    code: codeVersionOf(c) || "—",
    behind: !!(codeVersionOf(c) && newest && codeVersionOf(c) < newest),
    gallery: h.model?.reported || r.model_version || "—",
    analysis: r.analysis_mode || "—",
    windows: r.review_window_mode || "—",
    encoder: m.encoder ? `${m.encoder.engine || "?"}${m.encoder.self_check != null ? ` ${m.encoder.self_check}` : ""}` : (r.models ? "—" : "not reported"),
    detector: m.detector ? `${m.detector.name}${m.detector.loaded === false ? " (not loaded)" : ""} · ${m.detector.engine || "?"}` : (r.models ? "none" : "not reported"),
    weight: c.device?.weight ? "yes" : "—",
    temp: t?.has_sensor && t.celsius != null ? `${t.celsius.toFixed(1)}${t.out_of_range ? " !" : ""}` : "—",
    cpu: sys.cpu_temp_c != null ? `${sys.cpu_temp_c}` : "—",
    disk: sys.disk ? `${gb(sys.disk.free_bytes)} (${sys.disk.free_pct}%)` : "—",
    diskLow: sys.disk ? sys.disk.free_pct < 10 : false,
    mem: sys.memory ? `${sys.memory.used_pct}%` : "—",
    uptime: sys.uptime_s ? `${(sys.uptime_s / 3600).toFixed(0)} h` : "—",
    net: [h.ip_address || c.device?.ip_address, h.location || c.device?.location].filter(Boolean).join(" · ") || "—",
    drift: driftOf(c, c.type).map((d) => `${d.what}: ${d.actual}`).join("; "),
  };
}

const COLUMNS = [
  ["name", "Cabinet"], ["type", "Type"], ["board", "Board"], ["status", "Status"], ["problems", "Issues"], ["seen", "Last seen"],
  ["door", "Door"], ["session", "Session"], ["cameras", "Cameras"], ["code", "Release"], ["gallery", "Gallery"],
  ["analysis", "Analysis"], ["windows", "Windows"], ["encoder", "Encoder"], ["detector", "Detector"], ["drift", "Differs from type"],
  ["weight", "Weight"], ["temp", "Cabinet °C"], ["cpu", "CPU °C"], ["disk", "Disk free GB"], ["mem", "Memory"], ["uptime", "Uptime"], ["net", "IP · location"],
];

export function CabinetsTable({ cabinets, newestRuntime }) {
  const [q, setQ] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);
  const [sort, setSort] = useState({ key: "status", dir: 1 });
  const rows = useMemo(() => {
    let out = cabinets.map((c) => row(c, newestRuntime)).filter((r) => showRevoked || r.status !== "revoked");
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((r) => COLUMNS.some(([k]) => String(r[k] ?? "").toLowerCase().includes(needle)));
    }
    const key = sort.key;
    out.sort((a, b) => {
      const va = key === "status" ? ORDER[a.status] ?? 9 : a[key];
      const vb = key === "status" ? ORDER[b.status] ?? 9 : b[key];
      return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
    });
    return out;
  }, [cabinets, newestRuntime, q, showRevoked, sort]);
  const th = (key, label) => (
    <th key={key} className="cursor-pointer whitespace-nowrap select-none" onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
      {label}{sort.key === key ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
    </th>
  );
  return (
    <div className="card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">All cabinets</h2>
        <div className="flex items-center gap-3 text-xs">
          <input className="input w-56 py-1 text-xs" placeholder="search any column" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="flex items-center gap-1"><input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} /> revoked</label>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="table text-xs">
          <thead><tr>{COLUMNS.map(([k, l]) => th(k, l))}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="whitespace-nowrap">
                <td><Link className="text-sky-700 hover:underline" to={`/cabinets/${r.id}`}>{r.name}</Link></td>
                <td>{r.c.type ? <Link className="hover:underline" to={`/types/${r.c.type.id}`}>{r.type}</Link> : <span className={isEmulator(r.c) ? "" : "text-amber-700"}>{r.type}</span>}</td>
                <td>{r.board}</td>
                <td><span className={`rounded px-2 py-0.5 ${STATUS_STYLE[r.status] || STATUS_STYLE.unknown}`}>{r.status}</span></td>
                <td className="max-w-[16rem] truncate text-amber-700" title={r.problems}>{r.problems}</td>
                <td>{r.seen ? ago(r.seen) : "never"}</td>
                <td>{r.door}{r.charge ? <span className={r.charge === "relay stuck" ? "text-rose-700" : "text-slate-500"}> · charging {r.charge}</span> : null}</td>
                <td>{r.session}</td>
                <td className={r.camError ? "text-rose-700" : ""} title={r.camError}>{r.cameras}</td>
                <td className={`font-mono ${r.behind ? "text-amber-700" : ""}`}>{r.code}</td>
                <td className="font-mono">{r.gallery}</td>
                <td>{r.analysis}</td>
                <td>{r.windows}</td>
                <td>{r.encoder}</td>
                <td>{r.detector}</td>
                <td className="max-w-[14rem] truncate text-amber-700" title={r.drift}>{r.drift || (r.c.type?.expected && Object.keys(r.c.type.expected).length ? <span className="text-emerald-700">matches</span> : "—")}</td>
                <td>{r.weight}</td>
                <td>{r.temp}</td>
                <td>{r.cpu}</td>
                <td className={r.diskLow ? "text-amber-700" : ""}>{r.disk}</td>
                <td>{r.mem}</td>
                <td>{r.uptime}</td>
                <td>{r.net}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate-500">Click a column to sort. Encoder and detector come from the cabinet's own report (release 2026.10.22-120 or later).</p>
    </div>
  );
}
